import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  Container,
  DisplayText,
  Form,
  theme,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';

import {
  GroupOrderLocationInfoPressable,
  GroupOrderModalCloseButton,
  GroupOrderModal,
  GroupOrderModalContent,
  GroupOrderModalFooter,
  GroupOrderModalScrollView,
} from '@order/features/group-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { GroupOrderSignedOutModal, LoadingAnimation, NotFoundView } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useGroupOrderCreateOrderMutation } from './graphql/GroupOrderLocation.generated';
import { GroupOrderRouteParams } from './GroupOrderSetupScreen.types';
import {
  type GroupOrderSetupFormData,
  useGroupOrderLocationDetails,
  useGroupOrderSetupForm,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderSetupScreen = () => {
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');
  const router = useRouter();

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─── Handlers ────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    router.back();
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isGroupOrderingEnabled) {
    return <NotFoundView />;
  }

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <GroupOrderSignedOutModal onClose={closeModal} />;
  }

  return <GroupOrderSetupScreenContent />;
};

// ─────────────────────────────────────────────────────────────────────────────

const GroupOrderSetupScreenContent = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { match } = useResponsive();

  // ─── Params ──────────────────────────────────────────────────────────

  const params = useLocalSearchParams<GroupOrderRouteParams>();

  const { restaurantId, deliveryAddressId } = params ?? {};

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [, createOrder] = useGroupOrderCreateOrderMutation();

  // ─── Derived ─────────────────────────────────────────────────────────

  const locationDetails = useGroupOrderLocationDetails({ deliveryAddressId, restaurantId });

  // ─── Form ────────────────────────────────────────────────────────────

  const { form, maxLengths, rules } = useGroupOrderSetupForm();
  const { control, formState } = form;
  const { errors, isValid, isSubmitting } = formState;

  // ─── Handlers ────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    router.back();
  }, [router]);

  const navigateToLocationSearch = useCallback(() => {
    router.navigate('/group-ordering/create/search-location');
  }, [router]);

  const onCreateOrderError = useCallback(() => {
    addNoticeBanner(
      {
        id: GROUP_ORDER_CREATE_ERROR_BANNER_ID,
        text: formatMessage(messages.createOrderError),
        palette: 'caution',
        autoHideTimeout: 1800,
      },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  const submitGroupOrder = useCallback(
    async (formData: GroupOrderSetupFormData) => {
      const location = locationDetails?.location;
      const restaurantId = Number(location?.restaurant?.id);

      if (!restaurantId) return;

      if (!Number.isFinite(restaurantId)) {
        onCreateOrderError();

        return;
      }

      const response = await createOrder({
        input: {
          restaurantId,
          deliveryOrderDetail: locationDetails?.deliveryOrderDetail,
          multipartyOrderDetails: { orderName: formData.orderName, hostName: formData.hostName },
        },
      });

      const result = response.data?.createOrder;

      if (response.error || result?.__typename !== 'CreateOrderSuccess') {
        onCreateOrderError();

        return;
      }

      const joinCode = result.order.multipartyDetails?.joinCode;

      if (!joinCode) {
        onCreateOrderError();

        return;
      }

      router.replace({ pathname: '/group-ordering/share', params: { joinCode } });
    },
    [createOrder, locationDetails, router, onCreateOrderError],
  );

  // ─── Loading State ───────────────────────────────────────────────────

  if (locationDetails?.isFetching) {
    return (
      <GroupOrderModal onClose={closeModal}>
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={match(['large', 'small'])} />
        </View>
      </GroupOrderModal>
    );
  }

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, match([undefined, styles.containerSM])];

  // ─────────────────────────────────────────────────────────────────────

  const isSubmitDisabled = !isValid || !locationDetails?.location;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GroupOrderModal onClose={closeModal}>
      <GroupOrderModalContent>
        <GroupOrderModalScrollView>
          <Container style={containerStyles}>
            <View>
              <GroupOrderModalCloseButton
                palette="muted"
                onPress={closeModal}
                accessibilityLabel={formatMessage(messages.closeA11yLabel)}
                withBottomMargin
              />

              <DisplayText sizeMatch={['32']}>
                <FormattedMessage {...messages.title} />
              </DisplayText>

              <BodyText sizeMatch={['16']} style={styles.subtitle}>
                <FormattedMessage {...messages.subtitle} />
              </BodyText>
            </View>

            <View style={styles.section}>
              <BodyText sizeMatch={['16']} bold>
                <FormattedMessage {...messages.details} />
              </BodyText>

              <View style={styles.fields}>
                <Form.TextFieldWithBackground
                  name="orderName"
                  control={control}
                  rules={rules.orderName}
                  accessibilityLabel={formatMessage(messages.orderNameLabel)}
                  placeholder={formatMessage(messages.orderNameLabel)}
                  notice={errors.orderName?.message}
                  maxLength={maxLengths.orderName}
                  hideSubmitButton
                  disabled={isSubmitting}
                  autoCapitalize="words"
                  clearButtonType="icon"
                />

                <Form.TextFieldWithBackground
                  name="hostName"
                  control={control}
                  rules={rules.name}
                  accessibilityLabel={formatMessage(messages.hostNameLabel)}
                  label={formatMessage(messages.hostNameLabel)}
                  helperText={formatMessage(messages.hostNameHelperText)}
                  notice={errors.hostName?.message}
                  maxLength={maxLengths.name}
                  hideSubmitButton
                  disabled={isSubmitting}
                  autoCapitalize="words"
                  clearButtonType="icon"
                />
              </View>
            </View>

            <View style={styles.divider} />

            <View style={styles.section}>
              <BodyText sizeMatch={['16']} bold>
                <FormattedMessage
                  {...messages.locationTitle}
                  values={{ channel: locationDetails?.orderChannel }}
                />
              </BodyText>

              <GroupOrderLocationInfoPressable
                accessibilityLabel={formatMessage(messages.locationToggleA11yLabel)}
                disabled={isSubmitting}
                orderChannel={locationDetails?.orderChannel}
                label={
                  locationDetails?.location?.label ?? formatMessage(messages.locationPlaceholder)
                }
                address={locationDetails?.location?.address ?? undefined}
                secondaryAddress={locationDetails?.location?.secondaryAddress ?? undefined}
                onPress={navigateToLocationSearch}
              />
            </View>
          </Container>
        </GroupOrderModalScrollView>

        <GroupOrderModalFooter>
          <Button
            size="large-wide"
            disabled={isSubmitDisabled}
            isLoading={isSubmitting}
            onPress={form.handleSubmit(submitGroupOrder)}
          >
            <FormattedMessage {...messages.submitCta} />
          </Button>
        </GroupOrderModalFooter>
      </GroupOrderModalContent>
    </GroupOrderModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Group Ordering',
    description: 'Group Order Setup | Title',
  },
  subtitle: {
    defaultMessage: 'Confirm your group order details and send invites.',
    description: 'Group Order Setup | Subtitle',
  },
  closeA11yLabel: {
    defaultMessage: 'Close group ordering',
    description: 'Group Order Setup | Close button accessibility label',
  },
  details: {
    defaultMessage: 'Details',
    description: 'Group Order Setup | Details section title',
  },
  orderNameLabel: {
    defaultMessage: 'Group order name',
    description: 'Group Order Setup | Order name field label',
  },
  hostNameLabel: {
    defaultMessage: 'Your name',
    description: 'Group Order Setup | Host name field label',
  },
  hostNameHelperText: {
    defaultMessage: 'Your name, shown to your group',
    description: 'Group Order Setup | Host name helper text',
  },
  locationTitle: {
    defaultMessage:
      '{channel, select, delivery {Delivery location} outpost {Outpost location} pickup {Pickup location} other {Location}}',
    description: 'Group Order Setup | Location section title',
  },
  locationPlaceholder: {
    defaultMessage: 'Choose location',
    description: 'Group Order Setup | Location placeholder',
  },
  locationToggleA11yLabel: {
    defaultMessage: 'Choose location',
    description: 'Group Order Setup | Location row accessibility label',
  },
  submitCta: {
    defaultMessage: 'Send invites',
    description: 'Group Order Setup | Submit CTA',
  },
  createOrderError: {
    defaultMessage: 'Unable to create group order',
    description: 'Group Order Setup | Create order error banner',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_CREATE_ERROR_BANNER_ID = 'group-order-create-error-banner';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
    gap: theme.spacing['8'],
  },
  containerSM: {
    paddingHorizontal: theme.spacing['8'],
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  },
  subtitle: {
    color: theme.colors.CHARCOAL,
  },
  section: {
    gap: theme.spacing['4'],
  },
  fields: {
    gap: theme.spacing['3'],
  },
  divider: {
    height: 10,
    marginHorizontal: -theme.spacing['8'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.colors.GRAY,
  },
});
